<template>
  <div class="page">
    <div class="heading"><div><h1>游戏服务器</h1><p>管理《星露谷物语》联机世界的运行状态与基本设置。</p><p v-if="status.serverDisplayName" class="server-display-name">服务器名称：<strong>{{ status.serverDisplayName }}</strong></p></div><el-button :loading="loading" @click="reload()">刷新状态</el-button></div>
    <div class="panel overview">
      <div><span class="caption">服务器状态</span><h2>{{ status.ready ? '游戏世界在线' : status.running ? '游戏正在准备中' : '游戏尚未启动' }}</h2><p>{{ status.message || '正在读取服务器状态' }}</p></div>
      <el-tag :type="status.ready ? 'success' : status.running ? 'warning' : 'info'" effect="plain">{{ status.containerState || '未初始化' }}</el-tag>
    </div>
    <div v-if="status.ready" class="world-stats">
      <div class="world-stat farm-stat"><span>当前农场（实际存档）</span><strong>{{ status.farmName || '未命名' }}</strong><p v-if="status.farmNamePending" class="farm-notice">新农场预设：<b>{{ status.configuredFarmName }}</b>。当前存档仍叫 {{ status.farmName }}，仅新建农场时应用预设名称。</p></div>
      <div class="world-stat"><span>在线人数</span><strong>{{ status.playerCount ?? 0 }} / {{ status.maxPlayers ?? '—' }}</strong></div>
      <div class="world-stat"><span>游戏日期</span><strong>{{ status.year || 1 }} 年 · {{ seasonLabels[status.season] || status.season || '—' }} · {{ status.day || 1 }} 日</strong></div>
      <div class="world-stat"><span>游戏时间</span><strong>{{ gameClock(status.timeOfDay) }}</strong></div>
    </div>
    <div class="panel invite-panel">
      <div class="section-heading">
        <div>
          <h3>Steam 联机邀请码</h3>
          <p class="hint">好友在游戏中选择「合作 → 加入 → 输入邀请码」即可尝试连接。</p>
        </div>
        <el-tag v-if="status.ready && status.steamRelayReady && !status.steamLobbyNeedsVerification" type="success" effect="plain">Steam 联机已就绪</el-tag>
        <el-tag v-else type="info" effect="plain">{{ status.ready && status.steamLobbyNeedsVerification ? 'Steam 大厅异常' : status.ready ? '联机服务准备中' : '服务器未就绪' }}</el-tag>
      </div>
      <div class="invite-copy-row">
        <el-input ref="inviteField" class="invite-input" :model-value="steamInviteCode"
          readonly size="large"  :placeholder="status.steamLobbyNeedsVerification ? 'Steam 大厅不可用' : status.ready ? '正在获取邀请码，请刷新状态' : '游戏启动后自动显示'" aria-label="Steam 联机邀请码" />
        <el-button type="primary" size="large" :disabled="!steamInviteCode" @click="copyInvite">
          复制邀请码
        </el-button>
      </div>
      <p v-if="status.ready && status.steamLobbyNeedsVerification" class="invite-warning">{{ status.steamLobbyWarning || status.message || 'Steam 大厅暂时不可用' }}。游戏世界与存档未停止，请勿通过反复重启尝试修复。</p>
      <p v-else-if="status.ready && status.steamRelayReady === false" class="invite-warning">
        游戏世界已启动，但 Steam 中继连接仍在建立，请稍后刷新。
      </p>
      <p v-else class="hint">邀请码由游戏服务器实时提供，无需打开 SSH 控制台；重启后如有变化会自动更新。</p>
    </div>
    <div v-if="status.installed && !status.ready && !status.running" class="notice"><template v-if="!status.imagesReady">官方游戏镜像尚未下载成功，需要先解决服务器连接 Docker Hub 的问题，再执行 <code>cd /opt/stradewAI/game && sudo docker compose -p stradew-ai pull server steam-auth</code>。</template><template v-else>游戏服务器已安装。如果之前已经完成 Steam 授权，直接点击下方“启动服务器”即可。仅首次授权时，才需在服务器 SSH 终端执行：<pre class="setup-commands">cd /opt/stradewAI/game
sudo docker compose -p stradew-ai stop server steam-auth
sudo docker compose -p stradew-ai run --rm -it steam-auth setup
sudo docker compose -p stradew-ai up -d steam-auth server</pre>授权后刷新本页面查看游戏启动状态。</template></div>
    <div v-if="status.running && !status.ready" class="notice">游戏进程正在启动或加载存档，请稍后刷新状态。可在下方查看实际启动日志。</div>
    <div class="panel quick-links">
      <h3>快捷管理</h3>
      <div class="quick-buttons">
        <router-link to="/settings">基础配置 / 中文 / 农场规则</router-link>
        <router-link to="/mods">MOD 管理</router-link>
        <router-link to="/saves">存档与恢复</router-link>
        <router-link to="/players">玩家管理</router-link>
      </div>
    </div>
    <div class="panel actions">
      <h3>运行控制</h3>
      <div class="buttons">
        <el-button type="primary" :loading="operating==='start'" :disabled="!!operating||status.running||!status.installed" @click="operate('start')">启动服务器</el-button>
        <el-button :loading="operating==='restart'" :disabled="!!operating||!status.running" @click="operate('restart')">重启</el-button>
        <el-button type="danger" plain :loading="operating==='stop'" :disabled="!!operating||!status.running" @click="operate('stop')">停止服务器</el-button>
      </div>
      <p class="hint">停止或重启会断开正在联机的玩家。执行前会再次确认。</p>
    </div>
    <div class="panel config-guide">
      <div class="section-heading">
        <div><h3>农场与联机设置</h3><p class="hint">改农场名称、人数、语言、地图和联机密码，都在这里统一管理。</p></div>
        <router-link to="/settings" class="settings-link">打开设置 →</router-link>
      </div>
    </div>
    <div class="panel">
      <div class="section-heading log-heading"><div><h3>运行日志</h3><p class="hint log-status"><span v-if="logsAutoRefresh" class="live-dot" aria-hidden="true"></span>{{ logsAutoRefresh ? '每 5 秒自动更新' : '自动更新已暂停' }}<span v-if="logsCheckedAt"> · 上次检查 {{ logsCheckedAt }}</span><span v-if="logsError" class="log-error"> · {{ logsError }}</span></p></div><div class="log-controls"><el-switch v-model="logsAutoRefresh" inline-prompt active-text="自动" inactive-text="暂停" aria-label="日志自动刷新" /><el-button v-if="!logFollow" size="small" plain @click="scrollLogsBottom">跳到最新</el-button><el-button size="small" :loading="logsLoading" @click="loadLogs()">刷新日志</el-button></div></div>
      <pre ref="logViewport" class="log" role="log" aria-label="服务器运行日志" @scroll="onLogScroll">{{ logs || '暂无日志。首次完成 Steam 授权并启动服务器后显示。' }}</pre>
    </div>
    <div class="panel server-delete">
      <div class="section-heading">
        <div><h3>重新创建游戏服务器</h3><p class="hint">如果想从第一天重新开始，可以删除当前服务器与农场世界，再按新设置创建。此操作不会删除网页管理平台。</p></div>
        <el-tag type="warning" effect="plain">谨慎操作</el-tag>
      </div>
      <div class="delete-preserve">
        <div><strong>将移除</strong><span>当前游戏服务器容器、正在使用的农场世界与游戏进度</span></div>
        <div><strong>将保留</strong><span>Steam 登录授权、已经下载的游戏、已上传的 MOD、服务器配置、旧世界备份</span></div>
      </div>
      <p v-if="status.running" class="hint">请先点击上方「停止服务器」，再进行删除，以免中断正在游玩的好友。</p>
      <p v-else class="hint">删除前系统会自动保存旧世界的完整备份。删除后，在「农场与联机设置」调整农场名称和地图，再点击「启动服务器」即可创建新世界。</p>
      <el-button type="danger" plain :loading="deleting" :disabled="status.running||!status.installed||!!operating" @click="deleteCurrentServer">删除当前服务器</el-button>
    </div>
  </div>
</template>
<script setup>
import { ref, reactive, computed, nextTick, onMounted, onUnmounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { http } from '../plugins/axios'
import { authErrorMessage } from '../auth'
const status=reactive({ installed:false,ready:false,running:false,inviteCode:'' })
const deleting=ref(false)
const inviteField=ref(null)
const steamInviteCode=computed(() => status.ready && !status.steamLobbyNeedsVerification && /^S[A-Za-z0-9]{6,31}$/.test(status.inviteCode || '')
  ? status.inviteCode : '')
async function copyInvite() {
  const value=steamInviteCode.value
  if (!value) return
  let copied=false
  if (navigator.clipboard?.writeText) {
    try { await navigator.clipboard.writeText(value); copied=true }
    catch (_) { /* Try selecting the readonly input as a fallback. */ }
  }
  if (!copied) {
    const field=inviteField.value?.input
    if (field) {
      field.focus()
      field.select()
      try { copied=document.execCommand('copy') } catch (_) { /* Manual selection remains possible. */ }
    }
  }
  if (copied) ElMessage.success('邀请码已复制，可以发给朋友了')
  else ElMessage.warning('无法自动复制，请选中上方邀请码手动复制')
}
const seasonLabels={spring:'春季',summer:'夏季',fall:'秋季',winter:'冬季'}
function gameClock(value) {
  if(!Number.isInteger(value)||value<0) return '—'
  return String(Math.floor(value/100)).padStart(2,'0')+':'+String(value%100).padStart(2,'0')
}
const loading=ref(false),logsLoading=ref(false),operating=ref(''),logs=ref('')
const logsAutoRefresh=ref(true),logFollow=ref(true),logViewport=ref(null),logsCheckedAt=ref(''),logsError=ref('')
let ticker,logsTicker
async function reload({silent=false}={}) {
  if(loading.value) return
  loading.value=true
  try {
    const {data}=await http.get('/api/manage/game/status',{params:{_t:Date.now()}})
    // Apply one complete snapshot to avoid temporarily hiding the farm every refresh.
    Object.assign(status,{ready:false,inviteCode:'',steamRelayReady:null,steamLobbyNeedsVerification:false,steamLobbyWarning:'',steamLobbyRegistered:null,serverDisplayName:'',configuredFarmName:'',farmNamePending:false},data)
  }
  catch(e) {
    if(!silent) ElMessage.error(authErrorMessage(e,'无法获取服务器状态'))
  }
  finally {loading.value=false}
}
function onLogScroll(){
  const element=logViewport.value
  if(element) logFollow.value=element.scrollHeight-element.scrollTop-element.clientHeight<40
}
async function scrollLogsBottom(){
  await nextTick()
  const element=logViewport.value
  if(element){element.scrollTop=element.scrollHeight;logFollow.value=true}
}
async function loadLogs({silent=false}={}) {
  if(logsLoading.value) return
  logsLoading.value=true
  const shouldFollow=logFollow.value
  try {
    const {data}=await http.get('/api/manage/game/logs',{params:{lines:200,_t:Date.now()},timeout:15000})
    logs.value=data.logs||''
    logsCheckedAt.value=new Date().toLocaleTimeString('zh-CN',{hour12:false})
    logsError.value=''
    if(shouldFollow)await scrollLogsBottom()
  }
  catch(e) {
    logsError.value='刷新失败，可手动重试'
    if(!silent) ElMessage.error(authErrorMessage(e,'日志暂不可用'))
    // Keep the last successful log visible even when the next poll fails.
  }
  finally {logsLoading.value=false}
}
function onVisibilityChange(){
  if(!document.hidden){reload({silent:true});if(logsAutoRefresh.value)loadLogs({silent:true})}
}
async function operate(action) {
  if(action !== 'start') {
    try {await ElMessageBox.confirm(action==='stop'?'确定停止游戏服务器？在线玩家会断开连接。':'确定重启游戏服务器？在线玩家会断开连接。','确认操作',{type:'warning',confirmButtonText:'确认执行',cancelButtonText:'取消'})}
    catch(_) {return}
  }
  operating.value=action
  try {const {data}=await http.post('/api/manage/game/'+action);ElMessage.success(data.message);await reload();await loadLogs()}
  catch(e) {ElMessage.error(authErrorMessage(e,'操作失败'))}
  finally {operating.value=''}
}
onMounted(async()=>{
  document.addEventListener('visibilitychange',onVisibilityChange)
  await Promise.all([reload(),loadLogs()])
  ticker=window.setInterval(()=>{
    if(!document.hidden)reload({silent:true})
  },15000)
  logsTicker=window.setInterval(()=>{
    if(!document.hidden && logsAutoRefresh.value)loadLogs({silent:true})
  },5000)
})
onUnmounted(()=>{
  window.clearInterval(ticker)
  window.clearInterval(logsTicker)
  document.removeEventListener('visibilitychange',onVisibilityChange)
})
async function deleteCurrentServer() {
  if(deleting.value || status.running)return
  try {
    await ElMessageBox.confirm(
      '当前农场的物品、金钱、建筑和游戏进度将从在线世界移除。系统会保留一份完整备份、Steam 登录和 MOD。删除后需要重新启动，才能创建新农场。',
      '确定删除当前游戏服务器？',
      {type:'warning',confirmButtonText:'继续确认',cancelButtonText:'取消'}
    )
    const {value}=await ElMessageBox.prompt(
      '为防止误操作，请在下面输入“删除服务器”。只有游戏已停止时才允许删除。',
      '再次确认删除',
      {confirmButtonText:'确认删除',cancelButtonText:'取消',inputPattern:/^删除服务器$/,inputErrorMessage:'请输入：删除服务器'}
    )
    if(value!=='删除服务器')return
  } catch (_) {return}
  deleting.value=true
  try {
    const {data}=await http.post('/api/manage/admin/server/reset',{confirmation:'删除服务器'},{timeout:120000})
    ElMessage.success(data.message||'当前服务器已移除，旧世界备份已保留')
    await reload()
  } catch(e) {ElMessage.error(authErrorMessage(e,'操作失败，原有农场未确认删除'))}
  finally {deleting.value=false}
}
</script>
<style scoped>
.page{max-width:1050px}.heading,.section-heading{display:flex;align-items:center;justify-content:space-between;gap:15px}.heading{margin-bottom:24px}h1{margin:0;font-size:27px}h2{margin:7px 0;font-size:24px}h3{font-size:17px;margin:0}.heading p,.overview p,.hint,.section-heading span,.inline-note{font-size:13px;color:#718278;line-height:1.7}.panel{padding:25px;background:white;border:1px solid #e7eee8;border-radius:14px;margin-bottom:18px}.overview{display:flex;justify-content:space-between;align-items:center}.overview p{margin-bottom:0}.caption{font-size:12px;color:#7b9581}.buttons{display:flex;gap:10px;flex-wrap:wrap;margin-top:18px}.hint{margin-bottom:0}.settings{margin:24px 0 6px;max-width:430px}.inline-note{margin-left:12px}.log{background:#15241b;color:#d4e8ce;border-radius:10px;overflow:auto;max-height:350px;min-height:115px;padding:17px;white-space:pre-wrap;word-break:break-all;font-size:12px;line-height:1.7;margin-top:18px}.notice{padding:14px 18px;border-radius:9px;border:1px solid #e6d5ab;background:#fffaf0;color:#85672f;margin-bottom:18px;font-size:13px;line-height:1.9}.world-stats{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin-bottom:18px}.world-stat{border:1px solid #e7eee8;border-radius:12px;background:#fff;padding:19px 15px;min-width:0}.world-stat span{display:block;color:#728477;font-size:12px;margin-bottom:10px}.world-stat strong{display:block;color:#254734;font-size:17px;font-weight:650;overflow-wrap:anywhere}.invite-panel{background:linear-gradient(120deg,#f5faf4,#fff 75%)}.invite-panel .section-heading{align-items:flex-start}.invite-panel .hint{margin:6px 0 0}.invite-copy-row{display:flex;gap:12px;align-items:center;margin-top:20px}.invite-input{max-width:390px;min-width:0;flex:1}.invite-input :deep(.el-input__inner){font-size:21px;font-weight:700;letter-spacing:2px;color:#234e34;font-variant-numeric:tabular-nums}.invite-copy-row .el-button{min-width:145px}.invite-copy-row .el-icon{margin-right:7px}.invite-warning{font-size:13px;color:#aa691e;margin:14px 0 0;line-height:1.7}.quick-buttons{display:flex;gap:10px;margin-top:16px;flex-wrap:wrap}.quick-buttons a{padding:10px 14px;border:1px solid #bad4bf;border-radius:8px;background:#f4faf4;color:#326047;font-size:13px}.quick-buttons a:hover{background:#e6f3e8}.notice code{overflow-wrap:anywhere;font-size:12px}.setup-commands{white-space:pre-wrap;overflow-wrap:anywhere;background:#fff;border:1px solid #e6d5ab;border-radius:8px;padding:12px;margin:11px 0 6px;color:#4d583f;font-size:12px;line-height:1.9}@media(max-width:850px){.world-stats{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:650px){.invite-copy-row{flex-direction:column;align-items:stretch}.invite-input{max-width:none;width:100%;flex:auto}.invite-copy-row .el-button{width:100%;margin:0}.invite-panel .section-heading{gap:9px}}@media(max-width:650px){.heading{align-items:flex-start;flex-wrap:wrap}.panel{padding:18px}.overview{align-items:flex-start;gap:10px;flex-wrap:wrap}.section-heading{flex-wrap:wrap}}
.server-delete{border:1px solid #eddacc!important;background:#fffcf6!important}.server-delete h3{color:#8c5542}.delete-preserve{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:16px 0}.delete-preserve>div{background:#f9f5eb;border-radius:12px;padding:14px;display:flex;flex-direction:column;gap:7px;font-size:12px;line-height:1.7;color:#6e7869}.delete-preserve strong{font-size:13px;color:#49634d}@media(max-width:650px){.delete-preserve{grid-template-columns:1fr}}
.config-guide{background:#fbfff7!important}.settings-link{font-size:13px;font-weight:700;color:#467950;white-space:nowrap}.settings-link:hover{text-decoration:underline}

.server-display-name{margin:6px 0 0!important;color:#52755d!important}.server-display-name strong{font-size:14px;color:#2f6842}.farm-stat{position:relative}.farm-notice{font-weight:400;font-size:11px;line-height:1.7;color:#856735;background:#fff9e8;border-radius:6px;padding:7px 9px;margin:10px 0 0}.farm-notice b{color:#486d45}.log-heading{align-items:center}.log-controls{display:flex;gap:10px;align-items:center;flex-wrap:wrap}.log-status{margin:6px 0 0!important;line-height:1.5}.log-error{color:#b36553}.live-dot{display:inline-block;width:7px;height:7px;border-radius:50%;background:#59a66a;margin-right:7px}@media(max-width:650px){.log-controls{justify-content:flex-start}}
</style>
